import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback } from "react";
import { AuthUser, Profile, AuthenticatedUser } from "@/types/auth";
import { supabase } from "@/integrations/supabase/client";
import { debugLogger } from '@/utils/debugLogger';
import { presenceService } from '@/core/PresenceService';
import { datadogRum } from '@datadog/browser-rum';

interface AuthContextType {
  user: AuthUser | null;
  profileData: Profile | null;
  loading: boolean;
  error: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  updateProfileData: (data: Partial<Profile>) => Promise<void>;
  refreshProfileData: () => Promise<Profile | null>;
  setUser: (user: AuthUser | null) => void;
  usuarioAutenticado: AuthenticatedUser | null;
  retry: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [profileData, setProfileData] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usuarioAutenticado, setUsuarioAutenticado] = useState<AuthenticatedUser | null>(null);

  debugLogger.debug('AuthProvider - Render:', { 
    hasUser: !!user,
    loading,
    error,
    currentPath: window.location.pathname
  });

  // Simplified profile fetch
  const fetchProfileData = useCallback(async (userId: string): Promise<Profile | null> => {
    try {
      debugLogger.debug('Auth: Fetching profile from database for:', userId);
      
      // Fetch profile data
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        debugLogger.error("Auth: Error fetching profile:", error);
        return null;
      }

      // If no profile found, try to create one via trigger by updating auth.users
      if (!data) {
        debugLogger.warn('Auth: Profile not found after LEFT JOIN', { userId });
        try {
          // First verify the user still exists in auth.users
          const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
          
          if (userError || !currentUser) {
            debugLogger.warn('Auth: User no longer exists in auth.users, forcing signout', { 
              userError: userError?.message,
              code: userError?.code 
            });
            await supabase.auth.signOut();
            return null;
          }
          
          // Update user metadata to trigger the handle_new_user function
          await supabase.auth.updateUser({
            data: { trigger_profile_creation: new Date().toISOString() }
          });
          
          // Wait a bit and try fetching again
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          const { data: retryData, error: retryError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .maybeSingle();
            
          if (!retryError && retryData) {
            debugLogger.debug('Auth: Profile created successfully via trigger');
            const profile = retryData as Profile;
            setProfileData(profile);
            return profile;
          }
        } catch (triggerError: any) {
          debugLogger.error('Auth: Error triggering profile creation:', triggerError);
          
          // If user_not_found error, force signout to clear invalid session
          if (triggerError?.code === 'user_not_found' || 
              triggerError?.message?.includes('User from sub claim in JWT does not exist')) {
            debugLogger.warn('Auth: User deleted from database, forcing logout to clear phantom session');
            await supabase.auth.signOut();
            return null;
          }
        }
        
        debugLogger.warn('Auth: No profile exists for user, returning null');
        return null;
      }

      // Phase 7: documentVerificationStatus now comes from ProfileContext via get_my_profile_complete
      const profile = data as Profile;
      
      setProfileData(profile);
      debugLogger.debug('Auth: Profile loaded with main_photo_url:', {
        userId,
        main_photo_url: profile.main_photo_url,
        hasMainPhoto: !!profile.main_photo_url
      });
      return profile;
    } catch (error) {
      debugLogger.error("Auth: Unexpected error fetching profile:", error);
      return null;
    }
  }, []);

  // Create authenticated user using only main_photo_url
  const createAuthenticatedUser = useCallback((currentUser: AuthUser, profile: Profile): AuthenticatedUser => {
    const authenticatedUser = {
      id: currentUser.id,
      email: currentUser.email,
      username: profile.username,
      fullName: currentUser.user_metadata?.full_name,
      avatarUrl: profile.main_photo_url, // Use main_photo_url as avatarUrl
      mainPhotoUrl: profile.main_photo_url,
      city: profile.city,
      state_code: profile.state_code,
      bio: profile.bio,
      birthDate: profile.birth_date,
      gender: profile.gender,
      orientation: profile.orientation,
      education: profile.education,
      occupation: profile.occupation,
      interests: profile.interests,
      lookingFor: profile.looking_for,
      onboardingCompleted: profile.onboarding_completed
    };

    debugLogger.debug('Auth: Created authenticated user with avatar:', {
      userId: currentUser.id,
      avatarUrl: authenticatedUser.avatarUrl,
      mainPhotoUrl: authenticatedUser.mainPhotoUrl,
      hasAvatar: !!authenticatedUser.avatarUrl
    });

    return authenticatedUser;
  }, []);

  // Process user after auth
  const processUser = useCallback(async (currentUser: AuthUser) => {
    debugLogger.debug('Auth: Processing user:', currentUser.id);
    
    try {
      // Fetch profile first
      const profile = await fetchProfileData(currentUser.id);
      
      // Note: Deactivation check is handled by ProtectedMainLayout
      // to avoid hard reload loops
      
      // Update presence (non-blocking)
      try {
        await presenceService.forceUpdate(currentUser.id, 'login');
      } catch (presenceError: any) {
        // Log as warning, not error - presence não deve bloquear login
        debugLogger.warn('Auth: Could not update presence on login:', {
          error: presenceError?.message,
          code: presenceError?.code
        });
      }
      
      if (profile) {
        const authenticatedUser = createAuthenticatedUser(currentUser, profile);
        setUsuarioAutenticado(authenticatedUser);
        
        // Set user context in Datadog RUM
        datadogRum.setUser({
          id: profile.id,
          email: currentUser.email || undefined,
          name: profile.username || undefined
        });
        
        debugLogger.debug('Auth: User processed successfully');
        
        // Check for inconsistent users (has stripe_customer_id but free plan) and sync
        const hasStripeCustomer = profile.stripe_customer_id;
        const hasFreeOrInconsistentPlan = profile.plan_type?.includes('free') || 
          (profile.role === 'sponsor' && profile.plan_type?.includes('sponsored')) ||
          (profile.role === 'sponsored' && profile.plan_type?.includes('sponsor'));
        
        if (hasStripeCustomer && hasFreeOrInconsistentPlan) {
          debugLogger.debug('Detected inconsistent user data:', {
            stripeCustomerId: profile.stripe_customer_id,
            role: profile.role,
            planType: profile.plan_type
          });
          console.log('User has inconsistent data, will sync when they visit subscriptions page');
        }
      }
    } catch (error) {
      debugLogger.error('Auth: Error processing user:', error);
    }
  }, [fetchProfileData, createAuthenticatedUser]);

  // Simple retry
  const retry = useCallback(() => {
    debugLogger.debug('Auth: Retrying...');
    setError(null);
    setLoading(true);
    
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        debugLogger.error('Auth: Retry error:', error);
        setError('Erro de autenticação');
        setLoading(false);
        return;
      }
      
      const currentUser = session?.user || null;
      setUser(currentUser);
      
      if (currentUser) {
        processUser(currentUser);
      }
      
      setLoading(false);
    });
  }, [processUser]);

  // ULTRA-SIMPLIFIED AUTH INITIALIZATION
  useEffect(() => {
    debugLogger.debug('Auth: Initializing...');
    
    let mounted = true;
    
    // Set up auth listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;
      
      debugLogger.debug('Auth: State change:', event, 'Session:', !!session);
      
      const currentUser = session?.user || null;
      setUser(currentUser);
      
      if (event === 'SIGNED_OUT') {
        // Clear everything on signout including RUM user
        datadogRum.clearUser();
        setProfileData(null);
        setUsuarioAutenticado(null);
        setError(null);
        setLoading(false);
      } else if (currentUser) {
        // Process user for any signin event
        processUser(currentUser);
        setLoading(false);
      } else {
        // No user
        setLoading(false);
      }
    });

    // Check initial session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (!mounted) return;
      
      if (error) {
        debugLogger.error('Auth: Initial session error:', error);
        setError('Erro de autenticação');
        setLoading(false);
        return;
      }
      
      const currentUser = session?.user || null;
      setUser(currentUser);
      
      if (currentUser) {
        processUser(currentUser);
      }
      
      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [processUser]);

  // Listen to real-time profile updates
  useEffect(() => {
    if (!user?.id) return;
    
    let unsubscribe: (() => void) | null = null;
    
    // Dynamically import to avoid circular dependencies
    import('@/core/RealtimeCentral').then(({ realtimeCentral }) => {
      unsubscribe = realtimeCentral.subscribe('auth-profile-listener', {
        id: 'auth-profile-listener',
        onProfileUpdate: (updatedProfile) => {
          debugLogger.debug('Auth: Profile updated via realtime:', updatedProfile);
          
          // Update profileData state preservando documentVerificationStatus
          setProfileData((prev) => {
            const prevSafe = (prev ?? {}) as Profile;
            return { ...prevSafe, ...(updatedProfile as Profile) };
          });
          
          // Update usuarioAutenticado with new profile data using callback to avoid dependencies
          setUsuarioAutenticado((prevUser) => {
            if (!prevUser) return prevUser;
            
            return {
              ...prevUser,
              username: updatedProfile.username || prevUser.username,
              mainPhotoUrl: updatedProfile.main_photo_url || prevUser.mainPhotoUrl,
              city: updatedProfile.city || prevUser.city,
              state_code: updatedProfile.state_code || prevUser.state_code
            };
          });
        }
      });
    });
    
    // ✅ Cleanup at the correct useEffect level
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user?.id]); // ✅ Simplified dependencies

  const signIn = async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
    });
  };

  const signOut = async () => {
    debugLogger.debug('Auth: Signing out');
    
    // Limpar RealtimeCentral ANTES do logout
    try {
      const { realtimeCentral } = await import('@/core/RealtimeCentral');
      
      debugLogger.debug('Auth: Limpando RealtimeCentral');
      realtimeCentral.cleanup();
      realtimeCentral.cleanup();
    } catch (error) {
      debugLogger.error('Auth: Erro limpando managers:', error);
    }
    
    if (user?.id) {
      try {
        // Phase 7: Use presenceService for consistent presence management
        await presenceService.forceUpdate(user.id, 'logout');
      } catch (error: any) {
        // Log as warning - logout deve continuar mesmo se presence falhar
        debugLogger.warn('Auth: Could not update presence on logout:', {
          error: error?.message,
          code: error?.code
        });
      }
    }
    
    await supabase.auth.signOut();
  };

  const updateProfileData = async (data: Partial<Profile>) => {
    if (!user) return;

    try {
      const { data: updatedData, error } = await supabase
        .from('profiles')
        .update(data)
        .eq('id', user.id)
        .select()
        .single();

      if (error) {
        debugLogger.error("Auth: Error updating profile:", error);
        throw error;
      }

      const typedData = updatedData as Profile;
      setProfileData(typedData);
      
      if (usuarioAutenticado) {
        const updatedAuthUser = createAuthenticatedUser(user, typedData);
        setUsuarioAutenticado(updatedAuthUser);
      }
    } catch (error) {
      debugLogger.error("Auth: Unexpected error updating profile:", error);
      throw error;
    }
  };

  const refreshProfileData = async (): Promise<Profile | null> => {
    if (!user) return null;
    return await fetchProfileData(user.id);
  };
  
  const value = {
    user,
    profileData,
    loading,
    error,
    signIn,
    signOut,
    updateProfileData,
    refreshProfileData,
    setUser,
    usuarioAutenticado,
    retry,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro do AuthProvider");
  }
  return context;
};
